import { initializeApp, type FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import { getMessaging, getToken, onMessage, type Messaging } from "firebase/messaging";

/**
 * Use the current hostname as authDomain when it's a known custom domain.
 * This way Google sign-in redirects show "behailay.com" instead of
 * "adgoy-family-prod.firebaseapp.com". Firebase Hosting already serves
 * the /__/auth/ handler on custom domains.
 */
const CUSTOM_AUTH_DOMAINS = [
  "adgoy.com", "www.adgoy.com",
  "behailay.com", "www.behailay.com",
  "adhanom.com", "www.adhanom.com",
];
const hostname = typeof window !== "undefined" ? window.location.hostname : "";
const authDomain = CUSTOM_AUTH_DOMAINS.includes(hostname)
  ? hostname
  : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: "528319163065",
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let messaging: Messaging | null = null;

function getApp(): FirebaseApp {
  if (!config.apiKey) {
    throw new Error(
      "Firebase config missing. Set VITE_FIREBASE_* in apps/web/.env.local",
    );
  }
  if (!app) app = initializeApp(config);
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(getApp());
    // Dev-only: point auth at the local Firebase Auth emulator when
    // VITE_AUTH_EMULATOR_URL is set (e.g. http://localhost:9199). Unset in
    // production builds, so this never runs there. Enables full phone-OTP
    // sign-in locally without SMS or reCAPTCHA.
    const emulatorUrl = import.meta.env.VITE_AUTH_EMULATOR_URL;
    if (emulatorUrl) {
      connectAuthEmulator(auth, emulatorUrl, { disableWarnings: true });
    }
  }
  return auth;
}

export function getFirebaseMessaging(): Messaging | null {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return null;
  if (!messaging) {
    try {
      messaging = getMessaging(getApp());
    } catch {
      return null;
    }
  }
  return messaging;
}

/**
 * Request notification permission and get FCM token.
 * Returns the token string or null if denied/unsupported.
 */
export async function requestNotificationToken(): Promise<string | null> {
  const m = getFirebaseMessaging();
  if (!m) return null;

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.warn("VITE_FIREBASE_VAPID_KEY not set, push notifications disabled");
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    // Register service worker
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");

    const token = await getToken(m, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.error("Failed to get FCM token:", err);
    return null;
  }
}

/**
 * Listen for foreground messages and show a notification.
 */
export function onForegroundMessage(callback: (payload: any) => void): () => void {
  const m = getFirebaseMessaging();
  if (!m) return () => {};
  return onMessage(m, callback);
}
